import { useRouter } from "next/router";
import { ReactElement, useState } from "react";
import { AlertCircle, ChevronRight, Search } from "react-feather";
import { Badge } from "src/components/rough/Badge";
import { CreatePsychoedDialog } from "src/components/rough/Builders/PsychoedBuilder/CreatePsychoedDialog";
import { Button } from "src/components/rough/Button";
import { Card } from "src/components/rough/Card";
import { Page } from "src/layouts/Page";
import { TextInput } from "src/components/rough/TextInput";
import { useAllPsychoeds } from "src/data/queries/psychoeds/usePsychoedQueries";
import { ID } from "src/types";
import { InterventionCardSkeleton } from "../../components/rough/Intervention/InterventionCardSkeleton";
import { NextPageWithLayout } from "../_app";

const Psychoeds: NextPageWithLayout = () => {
  const router = useRouter();
  const { data, loading, error } = useAllPsychoeds();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [query, setQuery] = useState("");
  const psychoedItems = data?.allPsychoeds.filter(
    (psychoed) =>
      psychoed.name.toLowerCase().includes(query.toLowerCase()) ||
      psychoed.description?.toLowerCase().includes(query.toLowerCase()) ||
      [psychoed.archived ? "archived" : "active", psychoed.draft ? "draft" : "published"].some(
        (t) => t.includes(query.toLowerCase()),
      ),
  );

  const editPsychoed = (id: ID) => {
    router.push("/psychoeds/[id]", `/psychoeds/${id}`);
  };

  return (
    <>
      <CreatePsychoedDialog
        open={createDialogOpen}
        onOpenChange={() => setCreateDialogOpen(false)}
      />
      <div className={"relative mx-auto flex h-full w-full max-w-[80rem] flex-col px-24"}>
        <div className="mb-4 mt-12 flex w-full items-center justify-between">
          <h1 className="text-3xl">Educational Materials</h1>
          <div className="flex items-center gap-2">
            <TextInput
              id="SearchInput"
              value={query}
              onValueChange={(e) => setQuery(e.target.value)}
              icon={
                <Search className="absolute bottom-2.5 left-2.5 top-2.5 text-gray-500" size={18} />
              }
              placeholder="Search"
              className="!h-10 md:w-64"
            />

            <Button
              text="Add new educational material"
              variant="primary"
              onClick={() => setCreateDialogOpen(true)}
            />
          </div>
        </div>

        <div className="h-full flex-1 animate-intro-fade overflow-auto">
          {loading ? (
            <div className={"flex flex-col gap-4 py-4"}>
              <InterventionCardSkeleton />
            </div>
          ) : (
            <>
              {error ? (
                <div
                  className={
                    "mt-12 flex h-60 flex-col items-center justify-center py-4 text-gray-500"
                  }>
                  <AlertCircle size="36" strokeWidth="1.25" />
                  <p className={"mt-2 text-sm text-gray-600"}>We had an unexpected error</p>
                  <p className={"mt-2 text-sm"}>{error.message}</p>
                </div>
              ) : (
                <>
                  {psychoedItems !== undefined && psychoedItems.length > 0 ? (
                    <div className={"flex h-full gap-4 overflow-hidden"}>
                      <div className={"flex h-full w-full flex-1 flex-col gap-2 overflow-auto"}>
                        {psychoedItems.map((psychoed, key) => (
                          <Card
                            key={key}
                            className={
                              "group animate-intro-fade cursor-pointer transition duration-standard ease-in-out hover:bg-white active:border-gray-100"
                            }
                            onClick={() => editPsychoed(psychoed.id)}>
                            <div className="flex select-none items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <h1 className="text-bold">{psychoed.name}</h1>
                                  <Badge>{psychoed.draft ? "Draft" : "Published"}</Badge>
                                  <Badge>{psychoed.archived ? "Archived" : "Active"}</Badge>
                                </div>

                                <p className="text-sm text-gray-500">{psychoed.description}</p>
                              </div>

                              <div className="flex translate-x-4 items-center gap-2 opacity-0 transition duration-standard ease-in-out group-hover:translate-x-0 group-hover:opacity-100 group-active:!translate-x-1">
                                <ChevronRight strokeWidth="1.2" />
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div
                      className={
                        "mt-12 flex h-60 flex-col items-center justify-center py-4 text-gray-500"
                      }>
                      <AlertCircle size="36" strokeWidth="1.25" />
                      <h4 className="mt-2 font-bold text-gray-900">
                        No educational materials found
                      </h4>
                      <p
                        className={
                          "text-sm text-gray-600"
                        }>{`We couldn't find any educational materials`}</p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

Psychoeds.getLayout = function getLayout(page: ReactElement) {
  return <Page>{page}</Page>;
};

export default Psychoeds;
